// src/pages/AppConfigPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './AppConfigPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import AppConfigModal from '../components/settings/AppConfigModal';
import {
    configPlatformLabel,
    fetchAppConfigs,
    readApiError,
    updateAppConfig,
} from '../services/appConfigApi';

// Zaman damgaları UTC geliyor; tarayıcının yerel saatine çeviriyoruz.
const formatDateTime = (iso) => {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

// Tabloda tek satırlık önizleme; tam değer düzenleme penceresinde.
const previewValue = (entry) => {
    if (entry.valueType === 'JSON') {
        try {
            return JSON.stringify(JSON.parse(entry.value));
        } catch {
            return entry.value;
        }
    }
    return entry.value;
};

const byKey = (a, b) => a.key.localeCompare(b.key);

const AppConfigPage = () => {
    const [entries, setEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [activeGroup, setActiveGroup] = useState(null);
    const [togglingIds, setTogglingIds] = useState(() => new Set());
    // null: kapalı, { entry: null }: yeni kayıt, { entry }: düzenleme
    const [modal, setModal] = useState(null);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const rows = await fetchAppConfigs();
            setEntries(rows);
            setLoadError(null);
        } catch (error) {
            const { message } = readApiError(error, 'Uzaktan konfigürasyon yüklenemedi.');
            setLoadError(message);
            if (silent) toast.error(message);
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Gruplar ayrı bir uçtan gelmiyor; tek GET'in sonucundan türetiliyor.
    const groups = useMemo(() => {
        const map = new Map();
        entries.forEach((entry) => {
            if (!map.has(entry.group)) map.set(entry.group, []);
            map.get(entry.group).push(entry);
        });
        return [...map.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, rows]) => ({
                name,
                rows: rows.sort(byKey),
                activeCount: rows.filter((r) => r.enabled).length,
            }));
    }, [entries]);

    const groupNames = useMemo(() => groups.map((g) => g.name), [groups]);

    // Seçili grup silinemez ama ilk yüklemede ya da liste değişince boş kalabilir.
    useEffect(() => {
        if (groups.length === 0) return;
        if (!activeGroup || !groupNames.includes(activeGroup)) setActiveGroup(groups[0].name);
    }, [groups, groupNames, activeGroup]);

    const current = groups.find((g) => g.name === activeGroup);

    const handleToggle = async (entry) => {
        const next = !entry.enabled;
        setTogglingIds((prev) => new Set(prev).add(entry.id));
        try {
            const updated = await updateAppConfig(entry.id, { enabled: next });
            setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, ...(updated || { enabled: next }) } : e)));
            toast.success(next
                ? `${entry.group}.${entry.key} yayına alındı.`
                : `${entry.group}.${entry.key} yayından kaldırıldı.`);
        } catch (error) {
            const { message } = readApiError(error, 'Durum değiştirilemedi.');
            toast.error(message);
        } finally {
            setTogglingIds((prev) => {
                const copy = new Set(prev);
                copy.delete(entry.id);
                return copy;
            });
        }
    };

    // Kaydettikten sonra listeyi yeniden çekiyoruz: updatedAt / updatedBy yalnızca
    // sunucuda üretiliyor. Grup seçimi yenilemeden SONRA: yeni açılan bir grup
    // liste gelmeden seçilirse geçersiz sayılıp ilk gruba sıfırlanır.
    const handleSaved = async (_result, { group }) => {
        setModal(null);
        await load({ silent: true });
        if (group) setActiveGroup(group);
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingSpinner />
                <p>Uzaktan konfigürasyon yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>Uzaktan Konfigürasyon</h1>
                    <p className={styles.description}>
                        Mobil uygulamanın açılışta çektiği ayarlar. Değişiklik için mağazaya sürüm çıkmak gerekmez.
                    </p>
                </div>
                <button type="button" className={styles.addButton} onClick={() => setModal({ entry: null })}>
                    + Yeni ayar
                </button>
            </div>

            <div className={styles.infoPanel}>
                <h2 className={styles.infoTitle}>Dikkat</h2>
                <ul className={styles.infoList}>
                    <li>
                        Değişiklik <strong>anında yayına girer</strong>; kullanıcı uygulamayı bir sonraki açışında yeni
                        değeri alır. Bakım modu gibi ayarlarda bunu bilerek kullanın.
                    </li>
                    <li>
                        Kayıtlar <strong>silinmez</strong>: eski uygulama sürümleri anahtarın orada olduğunu varsayar.
                        Kullanılmayan ayarı yayından kaldırın; kayıt panelde kalır, mobil yanıttan düşer.
                    </li>
                    <li>
                        Sürüm zorlama ayarları burada değil, <Link to="/app-versions">Uygulama Sürümleri</Link> sayfasında.
                    </li>
                </ul>
            </div>

            {loadError && (
                <div className={styles.errorPanel}>
                    <span>{loadError}</span>
                    <button type="button" className={styles.retryButton} onClick={() => load()}>
                        Tekrar dene
                    </button>
                </div>
            )}

            {!loadError && groups.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>Henüz hiç ayar yok.</p>
                    <button type="button" className={styles.addButton} onClick={() => setModal({ entry: null })}>
                        + İlk ayarı oluştur
                    </button>
                </div>
            ) : groups.length > 0 && (
                <>
                    <div className={styles.tabRow} role="tablist">
                        {groups.map((g) => (
                            <button
                                key={g.name}
                                type="button"
                                role="tab"
                                aria-selected={g.name === activeGroup}
                                className={`${styles.tab} ${g.name === activeGroup ? styles.tabActive : ''}`}
                                onClick={() => setActiveGroup(g.name)}
                            >
                                {g.name}
                                <span className={`${styles.tabCount} num`}>
                                    {g.activeCount === g.rows.length ? g.rows.length : `${g.activeCount}/${g.rows.length}`}
                                </span>
                            </button>
                        ))}
                    </div>

                    {current && (
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Anahtar</th>
                                        <th>Tip</th>
                                        <th>Platform</th>
                                        <th>Değer</th>
                                        <th>Durum</th>
                                        <th>Son değişiklik</th>
                                        <th aria-label="İşlemler" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {current.rows.map((entry) => {
                                        const updatedAt = formatDateTime(entry.updatedAt);
                                        return (
                                            <tr key={entry.id} className={entry.enabled ? '' : styles.rowDisabled}>
                                                <td className={styles.keyCell}>
                                                    <span className={styles.key}>{entry.key}</span>
                                                    {entry.description && (
                                                        <span className={styles.keyDescription}>{entry.description}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={styles.typeChip}>{entry.valueType}</span>
                                                </td>
                                                <td>
                                                    <span className={entry.platform === 'all' ? styles.platform : styles.platformScoped}>
                                                        {configPlatformLabel(entry.platform)}
                                                    </span>
                                                </td>
                                                <td className={styles.valueCell}>
                                                    {entry.valueType === 'BOOL' ? (
                                                        <span className={entry.value === 'true' ? styles.boolOn : styles.boolOff}>
                                                            {entry.value}
                                                        </span>
                                                    ) : entry.value === '' ? (
                                                        <span className={styles.valueEmpty}>(boş)</span>
                                                    ) : (
                                                        <span className={styles.value} title={entry.value}>
                                                            {previewValue(entry)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <StatusToggle
                                                        label={`${entry.group}.${entry.key} yayın durumu`}
                                                        checked={entry.enabled}
                                                        saving={togglingIds.has(entry.id)}
                                                        onChange={() => handleToggle(entry)}
                                                        onText="Yayında"
                                                        offText="Pasif"
                                                    />
                                                </td>
                                                <td className={styles.metaCell}>
                                                    {updatedAt && <span className="num">{updatedAt}</span>}
                                                    {entry.updatedBy && <span className={styles.metaBy}>{entry.updatedBy}</span>}
                                                    {!updatedAt && !entry.updatedBy && '—'}
                                                </td>
                                                <td className={styles.actionCell}>
                                                    <button
                                                        type="button"
                                                        className={styles.editButton}
                                                        onClick={() => setModal({ entry })}
                                                    >
                                                        Düzenle
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <AppConfigModal
                isOpen={!!modal}
                entry={modal?.entry || null}
                defaultGroup={activeGroup}
                groups={groupNames}
                entries={entries}
                onClose={() => setModal(null)}
                onSaved={handleSaved}
                onEditExisting={(existing) => setModal({ entry: existing })}
            />
        </div>
    );
};

export default AppConfigPage;
